import type { PropsWithChildren } from 'react'
import { useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useId } from '../hooks/useId'

type PortalProps =
  | {
      /**
       * If true, the portal will be accessible to screen readers
       * @default false
       */
      discoverable?: boolean
      /**
       * If true, the portal is rendered as a menu attached to a parent node
       */
      isMenu?: never
      /**
       * If true, the portal is displayed
       */
      open: boolean
      /**
       * The parent DOM node to attach the menu portal to. Required when isMenu is true
       */
      parent?: never
    }
  | {
      /**
       * If true, the portal will be accessible to screen readers
       * @default false
       */
      discoverable?: boolean
      /**
       * If true, the portal is rendered as a menu attached to a parent node
       */
      isMenu?: true
      /**
       * If true, the portal is displayed
       */
      open: boolean
      /**
       * The parent DOM node to attach the menu portal to. Required when isMenu is true
       */
      parent?: Node | null
    }

function usePortalRoot() {
  const id = `coconut-portal-root-${useId()}`

  return useState(() => {
    if (typeof window === 'undefined') return null
    const existingRoot = document.getElementById(id)
    if (existingRoot) return existingRoot

    const root = document.createElement('div')
    root.setAttribute('id', id)

    return document.body.appendChild(root)
  })[0]
}

function Portal({
  children,
  discoverable, // some portals should never be hidden from SRs
  isMenu,
  open,
  parent,
}: PropsWithChildren<PortalProps>): JSX.Element | null {
  const target = usePortalRoot()
  const [element] = useState<HTMLDivElement | null>(() =>
    typeof window === 'undefined' ? null : document.createElement('div'),
  )

  useLayoutEffect(() => {
    if (!target || !element || (isMenu && !parent)) return

    if (isMenu) {
      parent?.appendChild(element)
    } else {
      target.appendChild(element)
    }

    return () => {
      if (!target || !element) return

      if (isMenu) {
        parent?.removeChild(element)
      } else {
        target.removeChild(element)

        if (target.childNodes.length <= 0) {
          target.parentElement?.removeChild(target)
        }
      }
    }
  }, [target, element, isMenu, parent])

  useLayoutEffect(() => {
    if (element) {
      element.style.display = open ? 'block' : 'none'

      if (open && discoverable) {
        // react-focus-on adds these to other portals. use discoverable to override.
        element.parentElement?.removeAttribute('aria-hidden')
        element.parentElement?.removeAttribute('data-focus-on-hidden')
      }
    }
  }, [element, open, discoverable])

  return !target || !element || (isMenu && !parent)
    ? null
    : createPortal(children, element)
}

export default Portal
